import { axiosBase } from 'utils';

export function usersLoginService(email, password, externalToken) {
  const axios = axiosBase.create();
  return axios({
    method: 'POST',
    url: '/users/login',
    data: externalToken
      ? {
          externalToken: externalToken,
        }
      : {
          email: email,
          password: password,
        },
  });
}

export function usersListService() {
  const axios = axiosBase.create();
  return axios({
    method: 'GET',
    url: '/users',
  });
}

/** Super-admin-only: all MSP Super Admin-tier users in one list. */
export function usersSuperAdminListService(q) {
  const axios = axiosBase.create();
  return axios({
    method: 'GET',
    url: '/users/super-admins',
    params: q ? { q } : undefined,
  });
}

/**
 * Super-admin-only: request a short-lived token to "login as" another user
 * for debugging. The backend gates this to role 10.
 */
export function usersImpersonateService(id) {
  const axios = axiosBase.create();
  return axios({
    method: 'GET',
    url: '/users/impersonate/' + id,
  });
}

/**
 * Returns the currently authenticated user's record, skipping the
 * client/company data-guard that `/users/:id` applies. Useful for pages
 * (like the reports filter) that need to surface the logged-in user even
 * when they don't belong to the reseller currently being viewed.
 */
export function usersAcceptLegalService(values) {
  const axios = axiosBase.create();
  return axios({
    method: 'POST',
    url: '/users/accept-legal',
    data: {
      tosAccepted: values.tosAccepted,
      privacyAccepted: values.privacyAccepted,
      additionalAccepted: values.additionalAccepted,
    },
  });
}

export function usersVerifyTokenService() {
  const axios = axiosBase.create();
  return axios({
    method: 'GET',
    url: '/users/verify-token',
  });
}

export function usersLatestService() {
  const axios = axiosBase.create();
  return axios({
    method: 'GET',
    url: '/users/latest',
  });
}

export function usersListByIdService(id) {
  const axios = axiosBase.create();
  return axios({
    method: 'GET',
    url: '/users/' + id,
  });
}

export function usersDetailByIdService(id) {
  const axios = axiosBase.create();
  return axios({
    method: 'GET',
    url: '/users/detail/' + id,
  });
}

/**
 * The backend validator for user company/branch is
 * `Joi.string().allow('').optional()` — it accepts an empty string or a
 * missing field, but a literal `null` is rejected with "must be a string".
 * Super admin / workspace users intentionally have no company/branch, so we
 * coerce nullish values into an empty string before sending.
 */
const toOptionalString = (v) => (v == null ? '' : v);

export function usersCreateService(values) {
  const axios = axiosBase.create();
  return axios({
    method: 'POST',
    url: '/users',
    data: {
      is_ap_manager: values.is_ap_manager,
      email: values.email,
      password: values.password,
      role: values.role,
      managed_clients: values.managed_clients || [],
      company: toOptionalString(values.company),
      branch: toOptionalString(values.branch),
      title: values.title,
      notes: values.notes,
      photo: values.photo,
      display_name: values.display_name,
      first_name: values.first_name,
      last_name: values.last_name,
      cell_phone: values.cell_phone,
      phone: values.phone,
      ext: values.ext,
      fax: values.fax,
      hourly_rate: values.hourly_rate,
    },
  });
}

export function usersUpdateService(id, values) {
  const axios = axiosBase.create();
  return axios({
    method: 'PATCH',
    url: '/users/' + id,
    data: {
      is_ap_manager: values.is_ap_manager,
      email: values.email,
      role: values.role,
      managed_clients: values.managed_clients || [],
      company: toOptionalString(values.company),
      branch: toOptionalString(values.branch),
      title: values.title,
      notes: values.notes,
      photo: values.photo,
      display_name: values.display_name,
      first_name: values.first_name,
      last_name: values.last_name,
      cell_phone: values.cell_phone,
      phone: values.phone,
      ext: values.ext,
      fax: values.fax,
      hourly_rate: values.hourly_rate,
    },
  });
}

export function usersUpdatePasswordService(id, values) {
  const axios = axiosBase.create();
  return axios({
    method: 'PATCH',
    url: '/users/update-password/' + id,
    data: {
      password: values.password,
    },
  });
}

export function usersSetLoginClientService(id) {
  const axios = axiosBase.create();
  return axios({
    method: 'GET',
    url: '/users/set-login-client/' + id,
  });
}

export function usersSetLoginNetworkService(payload) {
  const axios = axiosBase.create();
  return axios({
    method: 'POST',
    url: '/users/set-login-network/',
    data: {
      locationId: payload.locationId,
      customerId: payload.customerId,
    },
  });
}

export function usersRoleService(companyId) {
  const axios = axiosBase.create();
  return axios({
    method: 'GET',
    url: `/users/list-roles/${companyId ? '?companyId=' + companyId : ''}`,
  });
}

export function usersGetLoginClientService() {
  const axios = axiosBase.create();
  return axios({
    method: 'GET',
    url: '/users/get-login-client/',
  });
}

export function usersGetLoginNetworkService() {
  const axios = axiosBase.create();
  return axios({
    method: 'GET',
    url: '/users/get-login-network/',
  });
}

export function usersDeleteService(id) {
  const axios = axiosBase.create();
  return axios({
    method: 'DELETE',
    url: '/users/' + id,
  });
}

export function usersSearchService(q) {
  const axios = axiosBase.create();
  if (q === '') {
    return axios({
      method: 'GET',
      url: '/users',
    });
  } else {
    return axios({
      method: 'GET',
      url: '/users/search?q=' + q,
    });
  }
}
